import React, { useState, useEffect, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";

import axiosInstance from "../../../Context/axiosInstance.js";
import { DarkModeContext } from "../../../Context/DarkModeContext.jsx";

function EditAnnouncementForm({ announcementId, onClose }) {
  const { isDarkMode } = useContext(DarkModeContext);

  const [announcement, setAnnouncement] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourses, setSelectedCourses] = useState([]);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchAnnouncement = useCallback(async () => {
    try {
      const announcementResponse = await axiosInstance.get(
        `/announcements/${announcementId}`,
      );
      setAnnouncement(announcementResponse.data);
      setNewTitle(announcementResponse.data.title);
      setNewDescription(announcementResponse.data.description);
      setSelectedCourses(announcementResponse.data.coursesIds || []);
    } catch (error) {
      toast.error("Error fetching announcement data");
    }
  }, [announcementId]); // Only depend on announcementId

  useEffect(() => {
    if (announcementId) {
      fetchAnnouncement();
    }
  }, [announcementId, fetchAnnouncement]); // Add fetchAnnouncement as a dependency

  // Wrap fetchCourses in useCallback to make it stable across renders
  const fetchCourses = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `/courses?page=1&pageSize=99999&sortColumn=Name&sortDirection=asc&search=${encodeURIComponent(
          searchQuery,
        )}`,
      );
      setCourses(response.data.response);
    } catch (error) {
      toast.error("Error fetching courses");
    }
  }, [searchQuery]); // Only depend on searchQuery

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]); // Now fetchCourses is stable and can be safely added as a dependency

  const handleCheckboxChange = (courseId) => {
    setSelectedCourses((prevSelectedCourses) => {
      const newSelectedCourses = prevSelectedCourses.includes(courseId)
        ? prevSelectedCourses.filter((id) => id !== courseId)
        : [...prevSelectedCourses, courseId];
      return newSelectedCourses;
    });
  };

  const saveEdit = async () => {
    if (newTitle.trim() === "" || newDescription.trim() === "") {
      toast.error("Both Title and Description must be filled.");
      return;
    }

    try {
      setLoadingEdit(true);
      await axiosInstance.put(`/announcements/${announcementId}`, {
        title: newTitle,
        description: newDescription,
        coursesIds: selectedCourses,
      });

      toast.success("Announcement updated successfully!");
      onClose();
    } catch (error) {
      toast.error("Error updating announcement.");
    } finally {
      setLoadingEdit(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  return (
    <div
      className={`w-[500px] h-[820px] max-w-full max-h-[calc(100dvh-2rem)] overflow-hidden ${
        showForm ? "slide-in" : "slide-out"
      }`}
    >
      <div
        className={`${
          isDarkMode ? "bg-[#292929]" : "bg-white"
        } w-full h-full min-w-0 rounded-[30px] p-4 sm:p-6 shadow-xl overflow-hidden`}
      >
        {announcement ? (
          <div className="h-full overflow-y-auto custom-scrollbar animate-fadeIn">
            <div className="w-full mb-6 text-center bg-blue-500 rounded-full text-white py-4">
              <p className="text-lg">Edit Announcement</p>
            </div>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="Title"
              className={`${
                isDarkMode
                  ? "bg-[#292929] border border-gray-600"
                  : "bg-gray-100 border border-gray-300"
              } w-full p-4 rounded-lg mb-4`}
            />
            <textarea
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              placeholder="Description"
              className={`${
                isDarkMode
                  ? "bg-[#292929] border border-gray-600"
                  : "bg-gray-100 border border-gray-300"
              } w-full min-h-[60px] max-h-[200px] p-4 rounded-lg`}
              rows="4"
            />

            <div
              className={`h-[385px] flex flex-col ${
                isDarkMode
                  ? "border-2 border-gray-600"
                  : "border-2 border-gray-300"
              } p-3 mt-5 rounded-md`}
            >
              <div className="shrink-0 pb-2">
                <input
                  type="text"
                  placeholder="Search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`${
                    isDarkMode
                      ? "bg-[#292929] border border-gray-600"
                      : "bg-gray-100 border border-gray-300"
                  } w-full px-4 py-2 rounded-lg`}
                />
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto custom-scrollbar">
                {courses.length > 0 ? (
                  courses.map((course) => (
                    <div
                      key={course.id}
                      className="flex items-center gap-2 p-1"
                    >
                      <input
                        type="checkbox"
                        id={`course-${course.id}`}
                        checked={selectedCourses.includes(course.id)}
                        onChange={() => handleCheckboxChange(course.id)}
                      />

                      <span className="text-sm">
                        {course.name}{" "}
                        {course.gradeLevel === "Custom" && (
                          <span className="text-red-500">
                            * Custom Course *
                          </span>
                        )}
                      </span>
                    </div>
                  ))
                ) : (
                  <div className="flex h-full items-center justify-center text-gray-500">
                    No courses found
                  </div>
                )}
              </div>
            </div>

            {/* Save or Cancel Buttons */}
            <div className="flex justify-center gap-4 mt-6">
              <button
                onClick={saveEdit}
                disabled={loadingEdit}
                className={`${
                  isDarkMode
                    ? "border-2 border-green-600 hover:bg-green-600"
                    : "bg-green-600 text-white hover:bg-green-400"
                } px-6 py-2 rounded-lg ease-in-out transition-all duration-300 disabled:opacity-50`}
              >
                {loadingEdit ? "Saving..." : "Save Changes"}
              </button>

              <button
                onClick={closeForm}
                disabled={loadingEdit}
                className="border-2 border-red-600 hover:bg-red-600
                px-6 py-2 rounded-lg ease-in-out transition-all duration-300 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="flex justify-center items-center h-full gap-4 my-4">
            <ClipLoader color="#2264E5" size={50} />
          </div>
        )}
      </div>
    </div>
  );
}

export default EditAnnouncementForm;
